import clsx from 'clsx';

import { Typography } from '@/ui';
import { formatShortDayMonthDate, toDateTimeRangeString } from '@/utils';

import type { Session } from '@/types';
import type { FC } from 'react';

type SessionCardInfoProps = {
  loading?: boolean;
  name?: string;
  notTranscribed?: boolean;
  presentation?: 'default' | 'matched';
  session?: Session;
};

const SessionCardInfo: FC<SessionCardInfoProps> = ({
  session = {} as Session,
  loading = false,
  name = '',
  notTranscribed = false,
  presentation = 'default',
}) => {
  const { startedAt, endedAt } = session;
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const hourRange = toDateTimeRangeString(
    new Date(startedAt),
    new Date(endedAt),
    timeZone,
    true,
  );
  const startedAtDate = formatShortDayMonthDate(startedAt);

  if (presentation === 'matched') {
    const dateLabel = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'long',
      weekday: 'long',
    }).format(new Date(startedAt));
    const kindLabel = session.isChemistryCall
      ? 'Chemistry Call'
      : session.title;

    return (
      <div className="flex min-w-0 flex-col justify-center">
        <Typography variant="subtitle1">{dateLabel}</Typography>
        <Typography className="text-cement-400" variant="caption">
          {kindLabel} • {name} · {hourRange}
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col justify-center pb-2 md:pb-0">
      <Typography
        className={clsx('my-2 md:my-0', {
          loading,
        })}
        variant="subtitle1"
      >
        {session.title}
      </Typography>
      <div className="flex">
        <Typography
          className={clsx('my-0 text-cement-400', {
            'loading-sm-long': loading,
          })}
          variant="body2"
        >
          {name} &middot; {startedAtDate} &middot; {hourRange}
          {notTranscribed && ' · Not transcribed'}
        </Typography>
      </div>
    </div>
  );
};

export default SessionCardInfo;
